import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaSearch, FaTimesCircle } from 'react-icons/fa';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const casinos = [
    { id: 'casinocasino', name: 'CasinoCasino', path: '/offers/casinocasino' },
    { id: 'mrplay', name: 'MrPlay', path: '/offers/mrplay' },
    { id: 'partycasino', name: 'PartyCasino', path: '/offers/partycasino' },
  ];

  const pages = [
    { name: 'Home', path: '/' },
    { name: 'Compare Offers', path: '/comparison' },
    { name: 'About Us', path: '/about' },
    { name: 'FAQ', path: '/faq' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results: any[] = [];

    // Search casinos
    casinos.forEach((casino) => {
      if (casino.name.toLowerCase().includes(query)) {
        results.push({ type: 'casino', ...casino });
      }
    });

    // Search pages
    pages.forEach((page) => {
      if (page.name.toLowerCase().includes(query)) {
        results.push({ type: 'page', ...page });
      }
    });

    setSearchResults(results);
  }, [searchQuery]);

  const handleSearchClick = () => {
    setIsSearchOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    handleSearchClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleSearchClose();
    } else if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0].path);
    }
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/comparison', label: 'Compare Offers' },
    { path: '/about', label: 'About Us' },
    { path: '/faq', label: 'FAQ' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
        isScrolled
          ? 'glass-effect shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/logo.webp"
              alt="GameObsidian"
              className="h-10 w-auto"
              width="40"
              height="40"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <span className="text-2xl font-bold text-gradient">GameObsidian</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`transition-colors ${
                  location.pathname === link.path
                    ? 'text-accent-primary font-semibold'
                    : 'text-dark-text hover:text-accent-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            {/* Search Button */}
            <button
              className="hidden md:block p-2 text-dark-text hover:text-accent-primary transition-colors"
              aria-label="Search"
              onClick={handleSearchClick}
            >
              <FaSearch className="text-xl" />
            </button>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-dark-text hover:text-accent-primary transition-colors"
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
                setIsSearchOpen(false);
              }}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <FaTimes className="text-xl" />
              ) : (
                <FaBars className="text-xl" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        {isSearchOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in">
            <div className="relative">
              <div className="flex items-center gap-2 bg-dark-card rounded-lg px-4 py-2 border border-dark-border">
                <FaSearch className="text-accent-primary" />
                <input
                  type="text"
                  placeholder="Search casinos, pages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent border-none outline-none text-dark-text placeholder-dark-muted"
                />
                {searchQuery && (
                  <button
                    onClick={handleSearchClose}
                    className="text-dark-muted hover:text-dark-text"
                  >
                    <FaTimesCircle />
                  </button>
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 glass-effect rounded-lg overflow-hidden z-50 max-h-64 overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleResultClick(result.path)}
                      className="w-full text-left px-4 py-3 hover:bg-accent-primary/20 transition-colors flex items-center gap-3"
                    >
                      <FaSearch className="text-accent-primary flex-shrink-0" />
                      <div>
                        <div className="font-semibold">{result.name}</div>
                        <div className="text-xs text-dark-muted capitalize">{result.type}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileMenuOpen && !isSearchOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in">
            <div className="flex flex-col gap-4">
              <button
                onClick={handleSearchClick}
                className="flex items-center gap-2 py-2 text-dark-text hover:text-accent-primary transition-colors"
              >
                <FaSearch />
                Search
              </button>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`py-2 transition-colors ${
                    location.pathname === link.path
                      ? 'text-accent-primary font-semibold'
                      : 'text-dark-text hover:text-accent-primary'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Desktop Search Modal */}
      {isSearchOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:flex hidden items-start justify-center pt-32 px-4"
          onClick={handleSearchClose}
        >
          <div
            className="glass-effect rounded-2xl p-6 w-full max-w-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <div className="flex items-center gap-3 bg-dark-card rounded-lg px-4 py-3 border border-dark-border">
                <FaSearch className="text-accent-primary text-xl" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search casinos, pages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-transparent border-none outline-none text-dark-text text-lg placeholder-dark-muted"
                />
                <button
                  onClick={handleSearchClose}
                  className="p-2 text-dark-muted hover:text-dark-text transition-colors"
                  aria-label="Close search"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
              
              {searchResults.length > 0 && (
                <div className="mt-4 glass-effect rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleResultClick(result.path)}
                      className="w-full text-left px-4 py-4 hover:bg-accent-primary/20 transition-colors flex items-center gap-4 border-b border-dark-border last:border-0"
                    >
                      <div className="bg-accent-primary/20 p-3 rounded-lg">
                        <FaSearch className="text-accent-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-lg">{result.name}</div>
                        <div className="text-sm text-dark-muted capitalize">{result.type}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchQuery && searchResults.length === 0 && (
                <div className="mt-4 text-center text-dark-muted py-8">
                  No results found for "{searchQuery}"
                </div>
              )}

              {!searchQuery && (
                <div className="mt-4 text-center text-dark-muted py-8">
                  Start typing to search...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;

